import { useEffect, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional, type PinnedVisitor } from '../contexts/QueueDispatchContext';
import {
  fullName,
  parkEntry,
  requeueStarted,
  undoConfirmMessage,
  unparkEntry,
  withinUndoWindow,
} from '../services/queue';

// The queue actions that are NOT about picking who goes next: park / unpark
// ("even weg"), undo-with-confirm, and resolving a pending move. Lifted out of
// NextUpWidget so the queue-mode QueuePanel can host exactly the same
// behaviour instead of growing a second copy of it — there is one
// implementation, rendered by whichever surface is on screen.

function operatorIdFromSession(): string | null {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : null;
  } catch {
    return null;
  }
}

export interface QueueAffordances {
  busy: boolean;
  note: string | null;
  setNote: (n: string | null) => void;
  /** Something happened at this location within the RPC's 5-minute window. */
  undoable: boolean;
  undoLabel: string;
  undoTitle: string;
  undoLast: () => Promise<void>;
  togglePark: (entry: { queue_entry_id: string; parked: boolean }) => Promise<void>;
  /** A visitor stopped for a move who has not landed anywhere yet. */
  pendingMove: PinnedVisitor | null;
  requeuePending: () => Promise<void>;
  openMove: () => void;
}

export function useQueueAffordances(): QueueAffordances {
  const queue = useQueueDequeue();
  const dispatch = useQueueDispatchOptional();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Undo stays reachable for the RPC's 5-minute window after our own
  // dispatch, OR after any recent dequeue at the location (a colleague's
  // start, a started row after the queue emptied). A ticker retires the link
  // without needing a re-render trigger.
  const ownFresh =
    dispatch?.lastDispatch && withinUndoWindow(dispatch.lastDispatch.at)
      ? dispatch.lastDispatch
      : null;
  const latestFresh =
    queue.latestDequeue && withinUndoWindow(queue.latestDequeue.last_event_at)
      ? queue.latestDequeue
      : null;
  const undoable = !!dispatch && (!!ownFresh || !!latestFresh);
  const [, setUndoTick] = useState(0);
  useEffect(() => {
    if (!undoable) return;
    const t = setInterval(() => setUndoTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [undoable]);

  const pendingMove = dispatch?.pendingMove ?? null;

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    setNote(null);
    try {
      await fn();
      queue.refresh();
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function undoLast() {
    if (!dispatch) return;
    setBusy(true);
    setNote(null);
    try {
      let outcome = await dispatch.undoLast();
      if (typeof outcome === 'object') {
        if (!window.confirm(undoConfirmMessage(outcome.predicted))) {
          return;
        }
        outcome = await dispatch.undoLast({ force: true });
        if (typeof outcome === 'object') return;
      }
      if (outcome === 'undone_with_stop') {
        setNote('Teruggezet in de wachtrij en de ervaring is gestopt.');
      } else if (outcome === 'undone') {
        setNote('Laatste wachtrij-actie teruggedraaid.');
      } else {
        setNote('Geen recente actie om ongedaan te maken (binnen 5 minuten).');
      }
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function togglePark(entry: { queue_entry_id: string; parked: boolean }) {
    const operatorId = operatorIdFromSession();
    if (!operatorId) {
      setNote('Geen operator in sessie. Log eerst in.');
      return;
    }
    await withBusy(async () => {
      if (entry.parked) await unparkEntry(entry.queue_entry_id, operatorId);
      else await parkEntry(entry.queue_entry_id, operatorId);
    });
  }

  async function requeuePending() {
    if (!dispatch || !pendingMove) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    const pm = pendingMove;
    await withBusy(async () => {
      await requeueStarted(pm.entry.queue_entry_id, operatorId);
      dispatch.setPendingMove(null);
      setNote(`${fullName(pm.entry)} staat weer in de wachtrij.`);
    });
  }

  function openMove() {
    if (dispatch && pendingMove) dispatch.openDispatch(null, { pinned: pendingMove });
  }

  // Name the PC the way the operator sees it on the dashboard. Our own
  // dispatch already carries the label; a row that came from the database only
  // has started_pc_name, so map its id through that PC's report when we can.
  const labelForPc = (pcId: string | null, fallback: string | null): string | null =>
    (pcId ? dispatch?.pcReports.get(pcId)?.pcLabel : null) ?? fallback ?? null;
  const latestLabel = latestFresh
    ? labelForPc(latestFresh.started_pc_id, latestFresh.started_pc_name)
    : null;
  const undoLabel = ownFresh
    ? `↩ Ongedaan: ${ownFresh.pcLabel}`
    : latestFresh
      ? `↩ Ongedaan: ${fullName(latestFresh)}${latestLabel ? ` (${latestLabel})` : ''}`
      : '';
  const undoTitle = ownFresh
    ? `Zet ${ownFresh.customerName} terug in de wachtrij en stop de ervaring op ${ownFresh.pcLabel}.`
    : 'Laatste start/verwijdering op deze locatie terugdraaien (vraagt om bevestiging als het niet jouw actie was).';

  return {
    busy,
    note,
    setNote,
    undoable,
    undoLabel,
    undoTitle,
    undoLast,
    togglePark,
    pendingMove,
    requeuePending,
    openMove,
  };
}
